import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import type { BulkImportTargetMode } from '@/services/bulk-import.service';

export interface CommitConfirmCounts {
  passExisting: number;
  passNew: number;
  acknowledgedWarnings: number;
  excludedUnacknowledgedWarnings: number;
  excludedFail: number;
  excludedConflict: number;
  totalConsidered: number;
}

interface BulkImportCommitConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  counts: CommitConfirmCounts;
  targetMode: BulkImportTargetMode;
  targetLotNumber?: string | null;
}

/**
 * Phase 2f-F (§5): shown before every `commit` call. Never claims all rows
 * will import — explicitly enumerates what is eligible vs. excluded, and
 * states plainly that the attempt may end `PARTIALLY_COMPLETED` (§21's own
 * frozen, non-error outcome — never framed here as a failure).
 */
export function BulkImportCommitConfirmDialog({
  open,
  onClose,
  onConfirm,
  isPending,
  counts,
  targetMode,
  targetLotNumber,
}: BulkImportCommitConfirmDialogProps) {
  const eligibleTotal = counts.passExisting + counts.passNew + counts.acknowledgedWarnings;
  const excludedTotal =
    counts.excludedUnacknowledgedWarnings + counts.excludedFail + counts.excludedConflict;

  return (
    <Modal open={open} onClose={isPending ? () => {} : onClose} title="Confirm Final Import">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-3 py-2">
            <p className="text-xs text-green-800">Existing Equipment Match</p>
            <p className="text-lg font-semibold text-green-800">{counts.passExisting}</p>
          </div>
          <div className="rounded-lg border border-teal-500/30 bg-teal-500/10 px-3 py-2">
            <p className="text-xs text-teal-800">New Equipment</p>
            <p className="text-lg font-semibold text-teal-800">{counts.passNew}</p>
          </div>
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
            <p className="text-xs text-amber-800">Acknowledged Warnings (included)</p>
            <p className="text-lg font-semibold text-amber-800">{counts.acknowledgedWarnings}</p>
          </div>
          <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
            <p className="text-xs text-muted-foreground">Total Considered</p>
            <p className="text-lg font-semibold text-foreground">{counts.totalConsidered}</p>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-muted/20 px-3 py-2.5 text-xs text-muted-foreground">
          <p className="font-medium text-foreground">
            {excludedTotal} row(s) will remain excluded from this attempt:
          </p>
          <ul className="mt-1 space-y-0.5">
            <li>{counts.excludedUnacknowledgedWarnings} unacknowledged Warning row(s)</li>
            <li>{counts.excludedFail} Fail row(s)</li>
            <li>{counts.excludedConflict} Conflict row(s)</li>
          </ul>
          <p className="mt-1.5">
            Excluded rows are never silently dropped — they remain visible and can be corrected and
            revalidated for a later Final Import attempt.
          </p>
        </div>

        {excludedTotal > 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-orange-500/30 bg-orange-500/10 px-3 py-2 text-xs text-orange-800">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-3.5 w-3.5" />
            <p>
              Not every row is eligible right now — this attempt may result in a{' '}
              <span className="font-medium">Partially Completed</span> session rather than fully
              Completed. That is expected behavior, not an error.
            </p>
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          {targetMode === 'NEW_LOT'
            ? 'Final Import may create a new Receiving Lot and Arrival for the eligible rows above, per the existing commit engine.'
            : `Final Import will add the eligible rows above to Receiving Lot ${targetLotNumber ?? '(target lot)'}.`}
        </p>

        <div className="flex justify-end gap-3 border-t border-border pt-4">
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isPending || eligibleTotal === 0}>
            {isPending && (
              <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
            )}
            Confirm Final Import
          </Button>
        </div>
      </div>
    </Modal>
  );
}
